import React, { useState, useEffect } from "react";
import { X, Eye, Calendar, Mail, Phone, MessageSquare, Filter, AlertCircle, ChevronDown, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";

const STATUS_OPTIONS = ["New", "Contacted", "In Progress", "Completed", "Closed"];

const CustomerEnquiries = ({ category }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStatusTab, setActiveStatusTab] = useState("All");

  useEffect(() => {
    fetchEnquiries();
  }, [category]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await axiosInstancev1.get(`/properties/enquiries${category ? `?category=${category}` : ''}`);
      if (response.data.success) {
        setEnquiries(response.data.enquiries);
      }
    } catch (error) {
      console.error("Failed to fetch enquiries", error);
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (enquiryId, status) => {
    try {
      const response = await axiosInstancev1.put(`/properties/enquiries/${enquiryId}/status`, { status });
      if (response.data.success) {
        toast.success(`Status updated to ${status}`);
        setEnquiries(prev => prev.map(enq => enq.id === enquiryId ? { ...enq, status } : enq));
      }
    } catch (error) {
      console.error("Failed to update status", error);
      toast.error("Failed to update status");
    }
  };

  const handleViewClick = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedEnquiry(null);
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesSearch =
      enquiry.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enquiry.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enquiry.mobile?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enquiry.property_title?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = activeStatusTab === "All" || enquiry.status === activeStatusTab;

    return matchesSearch && matchesStatus;
  });

  const getStatusStyles = (status) => {
    switch (status) {
      case "New": return "bg-blue-100 text-blue-700";
      case "Contacted": return "bg-purple-100 text-purple-700";
      case "In Progress": return "bg-yellow-100 text-yellow-700";
      case "Completed": return "bg-green-100 text-green-700";
      case "Closed": return "bg-gray-100 text-gray-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusCount = (status) => {
    if (status === "All") return enquiries.length;
    return enquiries.filter(enq => (enq.status || "New") === status).length;
  };

  const StatusDropdown = ({ currentStatus, onChange }) => (
    <div className="relative group/status inline-block">
      <select
        value={currentStatus || "New"}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none pl-2.5 pr-7 py-1 rounded-full text-[10px] font-semibold cursor-pointer focus:outline-none ring-1 ring-inset ring-gray-200 transition-all ${getStatusStyles(currentStatus || "New")}`}
      >
        {STATUS_OPTIONS.map(opt => (
          <option key={opt} value={opt}>{opt.toUpperCase()}</option>
        ))}
      </select>
      <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
    </div>
  );

  const EnquiryCard = ({ enquiry }) => (
    <div className="bg-white border border-gray-100 rounded-[24px] p-5 mb-4 shadow-sm active:scale-[0.99] transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight">{enquiry.name}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400 font-medium uppercase tracking-wider">
            <Calendar className="w-3 h-3" />
            <span>{new Date(enquiry.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
        <StatusDropdown
          currentStatus={enquiry.status || "New"}
          onChange={(status) => handleUpdateStatus(enquiry.id, status)}
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="h-px flex-1 bg-gray-50"></div>
        <span className="text-[9px] font-semibold text-blue-500 bg-blue-50 px-3 py-0.5 rounded-full uppercase tracking-tighter shadow-sm border border-blue-100">
          {enquiry.property_title || "Property Post"}
        </span>
        <div className="h-px flex-1 bg-gray-50"></div>
      </div>

      <div className="grid grid-cols-1 gap-2 mb-5">
        <div className="flex items-center gap-3 p-2.5 bg-gray-50/50 rounded-xl border border-gray-50">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm border border-gray-100">
            <Mail className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Email Address</p>
            <p className="text-xs font-semibold text-gray-700 truncate">{enquiry.email || "No Email Provided"}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-2.5 bg-gray-50/50 rounded-xl border border-gray-50">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm border border-gray-100">
            <Phone className="w-3.5 h-3.5 text-green-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Phone Number</p>
            <p className="text-xs font-semibold text-gray-700">{enquiry.mobile}</p>
          </div>
        </div>
      </div>

      <button
        onClick={() => handleViewClick(enquiry)}
        className="w-full py-3 bg-blue-600 text-white rounded-[14px] text-[10px] font-bold tracking-widest uppercase hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100"
      >
        <Eye className="w-3.5 h-3.5" />
        VIEW FULL DETAILS
      </button>
    </div>
  );

  if (loading && enquiries.length === 0) {
    return (
      <div className="bg-white rounded-[24px] p-12 flex flex-col items-center justify-center shadow-sm border border-gray-100">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin" />
        </div>
        <p className="text-gray-400 font-semibold mt-4 text-[10px] uppercase tracking-widest">Loading Leads...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {category === 'housing' ? 'House Enquiries' : category === 'plot' ? 'Plot Enquiries' : 'Customer Enquiries'}
          </h2>
          <p className="text-xs text-gray-500">Manage and track your customer leads</p>
        </div>

        <div className="flex items-center gap-1.5 xs:gap-2 bg-white p-1 rounded-xl shadow-sm border border-gray-100 overflow-x-auto no-scrollbar">
          {["All", ...STATUS_OPTIONS].map(tab => {
            const count = getStatusCount(tab);
            return (
              <button
                key={tab}
                onClick={() => setActiveStatusTab(tab)}
                className={`px-2.5 xs:px-4 py-2 rounded-lg text-[10px] xs:text-xs font-semibold transition-all whitespace-nowrap ${activeStatusTab === tab
                  ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                  : "text-gray-500 hover:bg-gray-50"
                  }`}
              >
                {tab} <span className={`${activeStatusTab === tab ? 'text-blue-200' : 'text-gray-400'}`}>({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-100 rounded-lg text-xs xs:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all shadow-inner"
          />
          {searchQuery && (
            <X
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
              onClick={() => setSearchQuery("")}
            />
          )}
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      {filteredEnquiries.length > 0 ? (
        <>
          <div className="hidden lg:block bg-white overflow-hidden rounded-2xl shadow-lg border border-gray-100">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="py-4 px-6 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="py-4 px-6 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Lead Info</th>
                  <th className="py-4 px-6 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="py-4 px-6 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="py-4 px-6 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="hover:bg-blue-50/30 transition-colors group">
                    <td className="py-4 px-6">
                      <div className="text-xs font-semibold text-gray-800">{new Date(enquiry.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-medium">{new Date(enquiry.created_at).getFullYear()}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-xs font-semibold text-gray-900">{enquiry.name}</div>
                      <div className="flex flex-col gap-0.5 mt-1">
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                          <Mail className="w-3 h-3" />
                          {enquiry.email || <span className="italic text-gray-300 text-[10px]">No email</span>}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                          <Phone className="w-3 h-3" />
                          {enquiry.mobile}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="max-w-[180px]">
                        <div className="text-xs font-semibold text-blue-600 truncate" title={enquiry.property_title}>
                          {enquiry.property_title || "Deleted Property"}
                        </div>
                        <div className="text-[9px] text-gray-400 mt-0.5 flex items-center gap-1 font-medium">
                          <Clock className="w-2.5 h-2.5" /> ID: #{enquiry.listing_id}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <StatusDropdown
                        currentStatus={enquiry.status || "New"}
                        onChange={(status) => handleUpdateStatus(enquiry.id, status)}
                      />
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => handleViewClick(enquiry)}
                        className="p-1.5 bg-gray-50 text-gray-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-200"
                        title="View message"
                      >
                        <Eye className="w-4 h-4 mx-auto" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden space-y-4">
            {filteredEnquiries.map((enquiry) => (
              <EnquiryCard key={enquiry.id} enquiry={enquiry} />
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-2xl p-16 flex flex-col items-center justify-center shadow-lg border border-gray-100 text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-5">
            <AlertCircle className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No enquiries found</h3>
          <p className="text-gray-500 text-sm max-w-sm">We couldn't find any leads matching your current filters or search query.</p>
          <button
            onClick={() => { setSearchQuery(""); setActiveStatusTab("All"); }}
            className="mt-5 text-blue-600 font-semibold hover:underline text-sm"
          >
            Clear all filters
          </button>
        </div>
      )}

      <AnimatePresence>
        {showDetailModal && selectedEnquiry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4"
            onClick={closeDetailModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-[28px] overflow-hidden max-w-md w-full shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 text-white relative">
                <button
                  onClick={closeDetailModal}
                  className="absolute top-5 right-5 text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/10 rounded-xl backdrop-blur-md flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Enquiry Message</h3>
                    <p className="text-blue-100 text-[10px] font-medium uppercase tracking-widest mt-0.5">
                      {selectedEnquiry.status || "New"} Lead
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Date</p>
                    <p className="text-xs font-semibold text-gray-900">{new Date(selectedEnquiry.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Property ID</p>
                    <p className="text-xs font-semibold text-blue-600">#{selectedEnquiry.listing_id}</p>
                  </div>
                </div>

                <div className="space-y-3.5">
                  <div className="flex items-center gap-3.5 group">
                    <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 transition-colors">
                      <Mail className="w-4.5 h-4.5" />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase">Email Address</p>
                      <p className="text-xs font-semibold text-gray-900">{selectedEnquiry.email || "N/A"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3.5">
                    <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                      <Phone className="w-4.5 h-4.5" />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-gray-400 uppercase">Mobile Number</p>
                      <p className="text-xs font-semibold text-gray-900">{selectedEnquiry.mobile}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100/50">
                  <p className="text-[9px] font-bold text-blue-400 uppercase mb-2.5">Customer Message</p>
                  <p className="text-xs text-gray-700 leading-relaxed font-medium italic">
                    "{selectedEnquiry.message}"
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <a
                    href={`tel:${selectedEnquiry.mobile}`}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-lg shadow-green-100 transition-all"
                  >
                    <Phone className="w-3.5 h-3.5" /> CALL LEAD
                  </a>
                  <button
                    onClick={closeDetailModal}
                    className="px-5 py-3 bg-gray-100 text-gray-500 rounded-lg text-xs font-bold hover:bg-gray-200 transition-all"
                  >
                    DISMISS
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerEnquiries;
